export default customElements.define('w-field-slider', class FieldSlider extends wealthify.componentBases.Field {

    constructor () {
        const styleSelector = 'c-field-slider';
        // Range input controls are duplicated for the sake of maintaining
        // logical tab order when the layout/design changes at the mobile breakpoint.
        // Below that breakpoint, the number input appears first in the DOM, and second
        // on larger screens. This is achieved by having two sets of range controls in
        // the markup, and hiding each one appropriately with breakpoint based utility classes.
        // The range controls markup is stored in this variable to reduce repetition.
        const rangeControlsHTML = `
            <w-button class="${styleSelector}__input-button" aria-label="Decrease" label="<w-icon class='${styleSelector}__button-icon' type='minus'></w-icon><span class='u-display__hidden'>Decrease</span>" data-decrement></w-button>
            <input class="${styleSelector}__input-range" type="range" data-fieldslider />
            <w-button class="${styleSelector}__input-button" label="<w-icon class='${styleSelector}__button-icon' type='plus'></w-icon><span class='u-display__hidden'>Increase</span>" data-increment></w-button>
        `;
        const config = {
            templateHTML: `
            <fieldset class="${styleSelector}__container" data-root>
                <div data-fieldlabelcontainer>
                    <legend class="${styleSelector}__label" data-fieldlabel></legend>
                </div>
                <p class="${styleSelector}__label-pre" data-fieldlabelpre></p>

                <div class="${styleSelector}__input-container">
                    <div class="${styleSelector}__range-controls u-display__none--mobile">
                        ${rangeControlsHTML}
                    </div>
                    <div class="${styleSelector}__controls-container">
                        <w-field-numberbox class="${styleSelector}__input-number u-margin__bottom-mobile--0" is-label-hidden="true" label="Value" data-fieldnumber></w-field-numberbox>
                        <w-icon class="${styleSelector}__state-icon u-float--left" size="250" data-statusicon></w-icon>
                        <span data-statusaria></span>
                    </div>
                    <div class="${styleSelector}__range-controls u-display__none--tablet u-display__none--desktop">
                        ${rangeControlsHTML}
                    </div>
                </div>
                <p class="${styleSelector}__label-sub" data-fieldlabelsub></p>
                <w-message class="${styleSelector}__message" aria-live="assertive" data-fieldmessage></w-message>
                <input type="number" class="u-display__none" data-fieldinput/>
                <span class="u-display__hidden" aria-live="polite" data-accessiblebuttonvalue></span>
            </fieldset>
            `,
            cacheDom: ['data-fieldlabel', 'data-fieldlabelpre', 'data-fieldlabelsub', 'data-fieldinput', 'data-fieldnumber', 'data-fieldmessage', 'data-fieldslider', 'data-statusaria', 'data-statusicon', 'data-decrement', 'data-increment', 'data-accessiblebuttonvalue']
        };

        super(config);

        this.addEventListener(this.events.ValueChange.name, () => this.#setInputValues());
        this.addEventListener(this.events.ValidationChanged.name, () => {
            this.#setRangeAttributes();
            this.#updateRangeProgress();
            this.#enableValidSliderButtons();
        });

        this.onConnectedCallback = () => {
            if (!this.value) {
                this.value = this.validateMin || this.#defaultMin;
            }

            this.#setRangeAttributes();
            this.#setInputValues();
            this.#attachEventListeners();
            this.#updatePrefix();
            this.#updateSuffix();
        };
    }

    // Static properties
    static styleSelector = 'c-field-slider';

    static validationProps = [
        wealthify.FieldValidation.validationPropEnums.Min,
        wealthify.FieldValidation.validationPropEnums.Max,
        wealthify.FieldValidation.validationPropEnums.Step,
    ];

    static ranges = [
        [0, 10],
        [250, 50],
        [1000, 100],
        [15000, 1000],
        [100000, 10000]
    ];

    static get observedProps () {
        return [
            new wealthify.Prop('prefix', {default: ''}),
            new wealthify.Prop('suffix', {default: ''}),
        ];
    }

    // Instance properties

    get propCallbacks () {
        return {
            prefix: () => this.#updatePrefix(),
            suffix: () => this.#updateSuffix()
        };
    }

    get currentValueAdjustment() {
        let currentValueAdjustment;

        this.constructor.ranges.forEach(range => {
            if (this.value >= range[0]) {
                // Current value sits within range
                // (above or equal to min value)
                currentValueAdjustment = range[1];
            }
        });

        return currentValueAdjustment;
    }

    // Private

    #defaultMin = 0;
    #defaultMax = 100;
    #defaultStep = 1;

    #isValueOutOfBounds(value) {
        return parseInt(value) < this.validateMin || parseInt(value) > this.validateMax;
    }

    #attachEventListeners() {
        this.dom.fieldnumber.addEventListener('input', () => {
            if (!this.dom.fieldnumber.value) {
                this.value = this.dom.fieldnumber.value = 0;
            } else {
                this.value = this.dom.fieldnumber.value;
            }

            this.#updateRangeProgress();
        });
        this.dom.decrement.forEach(decrementButton => {
            decrementButton.onclickprop = () => this.#decrementValue();
        });
        this.dom.increment.forEach(incrementButton => {
            incrementButton.onclickprop = () => this.#incrementValue();
        });
        this.dom.fieldslider.forEach(slider => {
            slider.addEventListener('input', () => this.value = slider.value);
        });
    }

    #setInputValues() {
        if (this.dom.fieldnumber.value !== this.value) {
            this.dom.fieldnumber.value = this.value;
        }

        if (this.dom.fieldinput.value !== this.value) {
            this.dom.fieldinput.value = this.value;
        }

        this.dom.fieldslider.forEach(slider => {
            if (slider.value !== this.value) {
                slider.value = this.value;
            }
        });

        this.#updateRangeProgress();
        this.#enableValidSliderButtons();
    }

    #setRangeAttributes() {
        if (!this.validateMin) {
            this.validateMin = this.#defaultMin;
        }

        if (!this.validateMax === undefined) {
            this.validateMax = this.#defaultMax;
        }

        if (!this.validateStep) {
            this.validateStep = this.#defaultStep;
        }

        this.dom.fieldslider.forEach(slider => {
            slider.min = this.validateMin;
            slider.max = this.validateMax;
            slider.step = this.validateStep;
        });
    }

    #updatePrefix() {
        if (!this.dom.fieldnumber.isComponentReady) {
            return;
        }

        this.dom.fieldnumber.prefix = this.prefix;
    }

    #updateSuffix() {
        if (!this.dom.fieldnumber.isComponentReady) {
            return;
        }

        this.dom.fieldnumber.suffix = this.suffix;
    }

    #updateRangeProgress() {
        this.dom.fieldslider.forEach(slider => {
            const percentageWidth = (slider.value - this.validateMin) * 100 / (this.validateMax - this.validateMin);

            slider.max = this.validateMax;
            slider.style.backgroundSize =  `${percentageWidth}% 100%`;
        });
    }

    #decrementValue() {
        const clampedValue = this.#getClampedValue(wealthify.type.number.toNumber(this.value) - this.currentValueAdjustment);
        const rangeAdjustmentValue = this.currentValueAdjustment;
        const newValue = wealthify.type.number.toNumber(this.value) - rangeAdjustmentValue;

        this.value = this.#isValueOutOfBounds(newValue)
            ? clampedValue
            : newValue;

        this.dom.accessiblebuttonvalue.innerHTML = this.value;
    }

    #incrementValue() {
        const clampedValue = this.#getClampedValue(wealthify.type.number.toNumber(this.value) + this.currentValueAdjustment);
        const rangeAdjustmentValue = this.currentValueAdjustment;
        const newValue = wealthify.type.number.toNumber(this.value) + rangeAdjustmentValue;

        this.value = this.#isValueOutOfBounds(newValue)
            ? clampedValue
            : newValue;

        this.dom.accessiblebuttonvalue.innerHTML = this.value;
    }

    #getClampedValue(futureValue) {
        const modulo = futureValue % this.currentValueAdjustment;

        return wealthify.type.number.getClampedValue(futureValue - modulo, this.validateMin, this.validateMax);
    }

    #enableValidSliderButtons() {
        const isIncrementDisabled = parseInt(this.value) >= this.validateMax;
        const isDecrementDisabled = parseInt(this.value) <= this.validateMin;

        this.dom.increment.forEach(incrementButton => {
            incrementButton.disabledprop = isIncrementDisabled || this.disabledprop;
        });

        this.dom.decrement.forEach(decrementButton => {
            decrementButton.disabledprop = isDecrementDisabled || this.disabledprop;
        });
    }
});